import {
	Box,
	Button,
	Col,
	Divider,
	Grid,
	Icons,
	Inline,
	joinAttributes,
	Stack,
	Text,
} from "@sembark-travel/ui-base"
import {
	arrayMutators,
	FieldArray,
	Form,
	GetFieldValue,
	PercentageInputField,
	SelectField,
	SelectInputField,
	SubmissionError,
	SwitchInputField,
	TextInputField,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useXHR } from "@sembark-travel/xhr"
import pluralize from "pluralize"
import * as Validator from "yup"
import {
	SelectTenantCurrencyInputField,
	useFunctionalCurrencyOfTenant,
} from "../Currencies"
import { SelectCustomerPaymentReferences } from "./List"
import { getTotalAmountShare } from "./store"

interface ICustomerPaymentPreferenceFormValues {
	days_before_trip_start: {
		is_include: boolean
		condition: string
		days: number
	}
	trip_duration: {
		is_include: boolean
		condition: string
		nights: number
	}
	package_ranges: {
		is_include: boolean
		values: [
			{
				package_range_operator: string
				package_range_currency?: string
				package_range_amount: number
			},
		]
	}

	min_first_instalment_amount: {
		is_include: boolean
		values: [
			{
				amount: number
				currency?: string
			},
		]
	}

	breakdowns: {
		reference?: { id: number; name: string }
		day_offset: number
		instalment_breakup_in_percent: number
	}[]

	is_include_flight_cost_in_first_instalment: boolean
}

const customerPaymentPreferenceSchema = Validator.object({
	/* ---------------- Days before trip start ---------------- */
	days_before_trip_start: Validator.object({
		is_include: Validator.boolean().required(),

		condition: Validator.string().when("is_include", {
			is: true,
			then: (s) => s.required("Condition is required"),
			otherwise: (s) => s.optional(),
		}),

		days: Validator.number().when("is_include", {
			is: true,
			then: (s) =>
				s
					.typeError("Days must be a number")
					.integer("Days must be an integer")
					.min(0, "Days cannot be negative")
					.required("Days are required"),
			otherwise: (s) => s.optional(),
		}),
	}),

	/* ---------------- Trip duration ---------------- */
	trip_duration: Validator.object({
		is_include: Validator.boolean().required(),

		condition: Validator.string().when("is_include", {
			is: true,
			then: (s) => s.required("Condition is required"),
			otherwise: (s) => s.optional(),
		}),

		nights: Validator.number().when("is_include", {
			is: true,
			then: (s) =>
				s
					.typeError("Nights must be a number")
					.integer("Nights must be an integer")
					.min(0, "Nights cannot be negative")
					.required("Nights are required"),
			otherwise: (s) => s.optional(),
		}),
	}),

	/* ---------------- Package ranges ---------------- */
	package_ranges: Validator.object({
		is_include: Validator.boolean().required(),

		values: Validator.array().when("is_include", {
			is: true,
			then: (schema) =>
				schema
					.of(
						Validator.object({
							package_range_operator: Validator.string().required(
								"Condition is required"
							),

							package_range_currency: Validator.mixed().required(
								"Currency is required"
							),

							package_range_amount: Validator.number()
								.typeError("Amount must be a number")
								.positive("Amount must be greater than 0")
								.required("Amount is required"),
						})
					)
					.min(1, "At least one package range is required")
					.test(
						"unique-currency",
						"Each currency must be unique",
						(values = []) => {
							const currencies = values
								.map((v) => v?.package_range_currency)
								.filter(Boolean)

							return new Set(currencies).size === currencies.length
						}
					),
			otherwise: (schema) => schema.optional(),
		}),
	}),

	/* ---------------- Minimum first instalment ---------------- */
	min_first_instalment_amount: Validator.object({
		is_include: Validator.boolean().required(),

		values: Validator.array().when("is_include", {
			is: true,
			then: (schema) =>
				schema
					.of(
						Validator.object({
							amount: Validator.number()
								.typeError("Amount must be a number")
								.positive("Amount must be greater than 0")
								.required("Amount is required"),

							currency: Validator.mixed().required("Currency is required"),
						})
					)
					.min(1, "At least one currency is required")
					.test(
						"unique-currency",
						"Each currency must be unique",
						(values = []) => {
							const currencies = values.map((v) => v?.currency).filter(Boolean)

							return new Set(currencies).size === currencies.length
						}
					),
			otherwise: (schema) => schema.nullable(),
		}),
	}),

	/* ---------------- instalment breakdowns ---------------- */
	breakdowns: Validator.array()
		.of(
			Validator.object({
				reference: Validator.mixed().required("Reference is required"),

				day_offset: Validator.number()
					.typeError("Day offset must be a number")
					.integer("Day offset must be an integer")
					.required("Day offset is required"),

				instalment_breakup_in_percent: Validator.number()
					.typeError("Percentage must be a number")
					.integer("Percentage must be an integer")
					.min(1, "Minimum percentage is 1")
					.max(100, "Maximum percentage is 100")
					.required("Percentage is required"),
			})
		)
		.min(1, "At least one instalment is required")
		.test(
			"total-100",
			"Total instalment percentage must be exactly 100%",
			(breakdowns = []) =>
				breakdowns.reduce(
					(sum, b) => sum + (b.instalment_breakup_in_percent || 0),
					0
				) === 100
		),

	/* ---------------- Flags ---------------- */
	is_include_flight_cost_in_first_instalment: Validator.boolean().required(),
})

const validate = validateFormValues(customerPaymentPreferenceSchema)

function includeIf<
	T extends { is_include: boolean },
	R = Omit<T, "is_include">,
>(key: string, obj: T, mapper?: (value: Omit<T, "is_include">) => R) {
	if (!obj.is_include) return {}

	const { is_include, ...rest } = obj
	return { [key]: mapper ? mapper(rest) : rest }
}

const PACKAGE_OPERATOR_MAP: Record<string, number> = {
	is_greater: 1,
	is_less: 0,
}

function transformValues({
	values,
}: {
	values: ICustomerPaymentPreferenceFormValues
}) {
	const payload = {
		...includeIf("days_before_trip_start", values.days_before_trip_start),
		...includeIf("trip_duration", values.trip_duration),
		...includeIf(
			"min_first_instalment_amount",
			values.min_first_instalment_amount,
			({ values }) =>
				values.map((v) => ({ amount: v.amount, currency: v.currency }))
		),
		...includeIf("package_ranges", values.package_ranges, ({ values }) =>
			values.map((v) => ({
				package_range_operator: PACKAGE_OPERATOR_MAP[v.package_range_operator],
				package_range_currency: v.package_range_currency,
				package_range_amount: v.package_range_amount,
			}))
		),
		breakdowns: values.breakdowns.map((b) => ({
			reference: b.reference?.id,
			day_offset: b.day_offset,
			instalment_breakup_in_percent: b.instalment_breakup_in_percent,
		})),

		is_include_flight_cost_in_first_instalment:
			values.is_include_flight_cost_in_first_instalment ? 1 : 0,
	}
	return payload
}

export function NewCustomerPaymentPreference({
	onSuccess,
	onCancel,
}: {
	onSuccess: () => void
	onCancel: () => void
}) {
	const xhr = useXHR()
	const { currency: initialCurrency } = useFunctionalCurrencyOfTenant()
	return (
		<Box>
			<Form<ICustomerPaymentPreferenceFormValues>
				onSubmit={withServerErrors(async (values) => {
					const payload = transformValues({ values })
					await xhr.post("/customer-payment-preferences", payload)
					onSuccess?.()
					showSnackbar("Customer payment preference created successfully")
				})}
				initialValues={{
					days_before_trip_start: {
						is_include: false,
						condition: "is_less",
						days: 0,
					},
					trip_duration: {
						is_include: false,
						condition: "is_less",
						nights: 0,
					},
					package_ranges: {
						is_include: false,
						values: [
							{
								package_range_operator: "is_less",
								package_range_currency: initialCurrency,
								package_range_amount: 0,
							},
						],
					},
					breakdowns: [
						{
							reference: undefined,
							day_offset: 0,
							instalment_breakup_in_percent: 50,
						},
					],
					min_first_instalment_amount: {
						is_include: false,
						values: [
							{
								amount: 0,
								currency: initialCurrency,
							},
						],
					},

					is_include_flight_cost_in_first_instalment: false,
				}}
				validate={validate}
				mutators={{ ...arrayMutators }}
				subscription={{ submitting: true }}
			>
				{({ handleSubmit, submitting }) => {
					return (
						<form onSubmit={handleSubmit} noValidate>
							<Stack gap={"2"}>
								<Grid gap={"4"}>
									<Col xs={12} md={4}>
										<Stack maxWidth={"xs"}>
											<Text fontSize={"lg"} fontWeight={"semibold"}>
												Define Conditions
											</Text>
											<Text
												fontSize={"base"}
												color={"muted"}
												fontWeight="normal"
											>
												Set conditions to determine when this payment preference
												applies. All selected conditions must be met for this
												preference to be used
											</Text>
										</Stack>
									</Col>
									<Col>
										<Stack gap={"4"}>
											{/* days before trip start  */}
											<Stack gap={"4"}>
												<SwitchInputField
													name="days_before_trip_start.is_include"
													label={"Day before trip start"}
													help="Apply this preference only when booking within a certain number of days before trip start date."
												/>
												<GetFieldValue<ICustomerPaymentPreferenceFormValues> name="days_before_trip_start.is_include">
													{({ value }) =>
														value ? (
															<Inline gap={"4"}>
																<SelectInputField
																	name={"days_before_trip_start.condition"}
																	label="Condition"
																	defaultValue="is_less"
																>
																	<option value="is_less">Is less than</option>
																	<option value="is_greater">
																		Is greater than
																	</option>
																</SelectInputField>
																<Stack>
																	<TextInputField
																		label="Days"
																		name={"days_before_trip_start.days"}
																		required
																		type="number"
																	/>
																</Stack>
															</Inline>
														) : null
													}
												</GetFieldValue>
											</Stack>

											{/* trip duration  */}

											<Stack gap={"4"}>
												<SwitchInputField
													name="trip_duration.is_include"
													label={"Trip Duration (Number of Nights)"}
													help="Apply this preference based on how long the trip lasts. For example: Use different payment terms for trips longer than 7 nights."
												/>
												<GetFieldValue<ICustomerPaymentPreferenceFormValues> name="trip_duration.is_include">
													{({ value }) =>
														value ? (
															<Inline gap={"4"}>
																<SelectInputField
																	name="trip_duration.condition"
																	label="Condition"
																	defaultValue="is_less"
																>
																	<option value="is_less">Is less than</option>
																	<option value="is_greater">
																		Is greater than
																	</option>
																</SelectInputField>
																<Stack>
																	<TextInputField
																		label="Nights"
																		name="trip_duration.nights"
																		required
																		type="number"
																	/>
																</Stack>
															</Inline>
														) : null
													}
												</GetFieldValue>
											</Stack>

											{/* package ranges  */}
											<Stack gap={"4"}>
												<SwitchInputField
													name="package_ranges.is_include"
													label={"Package Amount Range"}
													help="Apply this preference only when the total package value falls above/below specific amount. Useful for setting different payment terms for high-value vs low-value bookings."
												/>
												<GetFieldValue<ICustomerPaymentPreferenceFormValues> name="package_ranges.is_include">
													{({ value }) =>
														value ? (
															<FieldArray name="package_ranges.values">
																{({ fields }) => (
																	<>
																		{fields.map((name, index) => (
																			<Inline gap={"4"} key={index}>
																				<SelectInputField
																					name={`${name}.package_range_operator`}
																					label="Condition"
																					defaultValue="is_less"
																				>
																					<option value="is_less">
																						Is less than
																					</option>
																					<option value="is_greater">
																						Is greater than
																					</option>
																				</SelectInputField>
																				<SelectTenantCurrencyInputField
																					name={`${name}.package_range_currency`}
																					label="Currency"
																					style={{ maxWidth: "150px" }}
																				/>
																				<Inline gap={"4"} alignItems="center">
																					<TextInputField
																						label="Amount"
																						name={`${name}.package_range_amount`}
																						required
																						type="number"
																					/>

																					{Number(fields.length || 0) > 1 ? (
																						<Button
																							onClick={() =>
																								fields.remove(index)
																							}
																							level="tertiary"
																						>
																							<Icons.Cancel />
																						</Button>
																					) : null}
																				</Inline>
																			</Inline>
																		))}
																		<Box>
																			<Button
																				onClick={() =>
																					fields.push({
																						package_range_operator: "is_less",
																						package_range_currency:
																							initialCurrency,
																						package_range_amount: 0,
																					})
																				}
																				size="sm"
																			>
																				<Icons.Plus />
																				Add More
																			</Button>
																		</Box>
																	</>
																)}
															</FieldArray>
														) : null
													}
												</GetFieldValue>
											</Stack>
										</Stack>
									</Col>
								</Grid>

								<Divider />

								{/* instalment breakdown */}

								<Grid gap={"4"}>
									<Col xs={12} md={4}>
										<Stack maxWidth={"xs"}>
											<Text fontSize={"lg"} fontWeight={"semibold"}>
												Instalments Breakdown
											</Text>
											<Text
												fontSize={"base"}
												color={"muted"}
												fontWeight="normal"
											>
												Define the payment instalments for your customers.
												Specify when each payment is due and what percentage of
												the total amount should be paid.
											</Text>
										</Stack>
									</Col>
									<Col>
										<Stack gap={"4"}>
											<Stack>
												<Text fontSize={"md"} fontWeight={"semibold"}>
													Please provide the details for the new payment
													preference that you need for a customer.
												</Text>
												<Text
													fontSize={"sm"}
													color={"muted"}
													fontWeight={"normal"}
												>
													Instalments are calculated from the Trip Conversion
													date (or Trip Hold date if applicable). Use positive
													days for payments after an event and negative days for
													before. Example: '-7' with Trip Start = payment due 7
													days before departure.
												</Text>
											</Stack>
											<SwitchInputField
												name="is_include_flight_cost_in_first_instalment"
												label={"Include flight cost in first instalment"}
												help="When enabled, 100% of flight cost will be added to the first instalment breakdown you set here, and remaining amount will be distributed according to the percentages you set."
											/>

											<Stack gap={"4"}>
												<SwitchInputField
													name="min_first_instalment_amount.is_include"
													label={"Include minimum first instalment amount"}
													help="When enabled, minimum first instalment amount will be added to the first instalment, and remaining amount will be distributed according to the percentages you set."
												/>

												<GetFieldValue<ICustomerPaymentPreferenceFormValues> name="min_first_instalment_amount.is_include">
													{({ value }) =>
														value ? (
															<FieldArray name="min_first_instalment_amount.values">
																{({ fields }) => (
																	<>
																		{fields.map((name, index) => (
																			<Inline gap={"4"} key={index}>
																				<SelectTenantCurrencyInputField
																					name={`${name}.currency`}
																					label="Currency"
																					style={{ maxWidth: "150px" }}
																				/>
																				<TextInputField
																					label="Amount"
																					name={`${name}.amount`}
																					required
																					type="number"
																				/>

																				{Number(fields.length || 0) > 1 ? (
																					<Button
																						onClick={() => fields.remove(index)}
																						level="tertiary"
																					>
																						<Icons.Cancel />
																					</Button>
																				) : null}
																			</Inline>
																		))}
																		<Box>
																			<Button
																				onClick={() =>
																					fields.push({
																						amount: 0,
																						currency: initialCurrency,
																					})
																				}
																				size="sm"
																			>
																				<Icons.Plus />
																				Add More
																			</Button>
																		</Box>
																	</>
																)}
															</FieldArray>
														) : null
													}
												</GetFieldValue>
											</Stack>

											<FieldArray<
												ICustomerPaymentPreferenceFormValues["breakdowns"][number]
											> name="breakdowns">
												{({ fields }) => (
													<Stack as="ol" gap={"2"}>
														{fields.map((name, index) => (
															<Box
																as="li"
																key={name}
																paddingY="2"
																borderBottomWidth="1"
															>
																<Grid gap="4">
																	<Col xs={12} sm>
																		<SelectField
																			select={SelectCustomerPaymentReferences}
																			name={`${name}.reference`}
																			label="Reference Event"
																			fetchOnMount
																			multiple={false}
																			placeholder="Select a reference..."
																		/>
																	</Col>
																	<Col xs={12} sm>
																		<TextInputField
																			label="Day offset from reference"
																			name={`${name}.day_offset`}
																			required
																			type="number"
																		/>
																	</Col>
																	<Col xs={12} sm style={{ minWidth: "220px" }}>
																		<PercentageInputField
																			label="Amount share from total amount"
																			name={`${name}.instalment_breakup_in_percent`}
																			required
																			type="number"
																			min={1}
																			max={100}
																		/>
																	</Col>
																	<Col xs={12} sm="auto">
																		{Number(fields.length) > 1 ? (
																			<Button
																				level="tertiary"
																				onClick={() => fields.remove(index)}
																				size="sm"
																			>
																				<Icons.Cancel /> Remove
																			</Button>
																		) : null}
																	</Col>
																</Grid>
															</Box>
														))}
														<GetFieldValue<
															ICustomerPaymentPreferenceFormValues["breakdowns"]
														> name="breakdowns">
															{({ value: breakdowns }) => {
																const totalAmountShare =
																	getTotalAmountShare(breakdowns)
																return totalAmountShare < 100 ? (
																	<Box as="li" key="add_more">
																		<Button
																			size="sm"
																			onClick={() =>
																				fields.push({
																					instalment_breakup_in_percent:
																						100 - totalAmountShare,
																					day_offset: 0,
																				})
																			}
																		>
																			<Icons.Plus /> Add More
																		</Button>
																	</Box>
																) : null
															}}
														</GetFieldValue>
													</Stack>
												)}
											</FieldArray>
										</Stack>
									</Col>
								</Grid>

								<Box>
									<GetFieldValue<
										ICustomerPaymentPreferenceFormValues["breakdowns"]
									> name="breakdowns">
										{({ value: breakdowns }) => (
											<PreferenceName breakdowns={breakdowns} />
										)}
									</GetFieldValue>
								</Box>

								<SubmissionError />

								<Divider />

								<Inline gap="2">
									<Button type="submit" level="primary" disabled={submitting}>
										{submitting ? "Saving..." : "Save Preference"}
									</Button>
									<Button type="button" level="tertiary" onClick={onCancel}>
										Cancel
									</Button>
								</Inline>
							</Stack>
						</form>
					)
				}}
			</Form>
		</Box>
	)
}
function getBreakdownName(
	breakdown: ICustomerPaymentPreferenceFormValues["breakdowns"][number]
): React.ReactNode {
	const { reference, day_offset, instalment_breakup_in_percent } = breakdown
	if (!reference || isNaN(day_offset) || isNaN(instalment_breakup_in_percent))
		return null
	return (
		<Box display="inline">
			<span>{instalment_breakup_in_percent}%</span>{" "}
			{day_offset !== 0 ? (
				<span>{pluralize("day", Math.abs(day_offset), true)}</span>
			) : null}{" "}
			<em>{day_offset < 0 ? " before" : day_offset > 0 ? " after" : " on"}</em>{" "}
			<span>{reference.name}</span>
		</Box>
	)
}

export function PreferenceName({
	breakdowns,
}: {
	breakdowns: ICustomerPaymentPreferenceFormValues["breakdowns"]
}) {
	const parts = breakdowns.map(getBreakdownName).filter((i) => i)
	if (!parts.length) return null
	return (
		<Box
			boxShadow="lg"
			rounded="lg"
			borderWidth="1"
			padding="4"
			bgColor="inset"
		>
			<Box
				textTransform="uppercase"
				fontWeight="semibold"
				letterSpacing="wider"
				color="muted"
				fontSize="sm"
			>
				Summary
			</Box>
			<Box fontWeight="semibold" fontSize="xl">
				{joinAttributes(...parts)}
			</Box>
		</Box>
	)
}
